import React from "react";
import {
    Drawer, Stack, Button, Inline, Text, Box, Dropdown, Card,
} from "braid-design-system";
import { useTranslation } from "react-i18next";
import { useToast } from "../../hooks/useToast";
import {
    PromotionFormData,
    PromotionToEdit,
    PromotionTypeOption,
} from "./types";
import PromotionHeaderSection from "./PromotionHeaderSection";
import PromotionOfferCriteriaSection from "./PromotionOfferCriteriaSection";
import PromotionDiscountSection from "./PromotionDiscountSection";
import {
    PromoHedRequest,
    getPromotionTypes,
} from "../../services/promotionService";

interface CreateUpdatePromotionDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    promotionToEdit?: PromotionToEdit;
    onSave: (formData: PromoHedRequest) => Promise<void>;
    onPromotionSaved: () => void;
    isReadOnly?: boolean;
}

const defaultFormData: PromotionFormData = {
    promo_code: '',
    promo_name: '',
    promo_desc: '',
    promo_type_id: null,
    promo_datefrom: '',
    promo_dateto: '',
    promo_timefrom: '',
    promo_timeto: '',
    promo_days_mask: 0,
    promo_active: true,
    offer_criteria: [],
    promo_disper: '',
    promo_disamt: '',
    promo_free_item: '',
    promo_free_voucher: '',
};

const isConfiguredCriteria = (oc: PromotionFormData['offer_criteria'][number]): boolean => {
    if (oc.criteria_type === 'amount_range') {
        return oc.amount_min != null || oc.amount_max != null;
    }
    return Boolean(oc.criteria_value && oc.criteria_value.trim().length > 0);
};

const toPromoHedRequest = (formData: PromotionFormData): PromoHedRequest => {
    const validDetails = formData.offer_criteria.filter(isConfiguredCriteria);

    return {
        promo_code: formData.promo_code,
        promo_name: formData.promo_name,
        promo_desc: formData.promo_desc || undefined,
        promo_type_id: formData.promo_type_id ?? 0,
        promo_datefrom: formData.promo_datefrom || null,
        promo_dateto: formData.promo_dateto || null,
        promo_timefrom: formData.promo_timefrom || null,
        promo_timeto: formData.promo_timeto || null,
        promo_days: formData.promo_days_mask || null,
        promo_active: formData.promo_active,
        promo_disper: formData.promo_disper ? Number(formData.promo_disper) : null,
        promo_disamt: formData.promo_disamt ? Number(formData.promo_disamt) : null,
        promo_free_item: formData.promo_free_item || null,
        promo_free_voucher: formData.promo_free_voucher || null,
        details: validDetails.map((oc, index) => ({
            id: oc.id,
            line_seq: index,
            criteria_type: oc.criteria_type,
            criteria_value: oc.criteria_value,
            amount_min: oc.amount_min ?? null,
            amount_max: oc.amount_max ?? null,
        })),
    };
};

const CreateUpdatePromotionDrawer: React.FC<CreateUpdatePromotionDrawerProps> = ({
    isOpen,
    onClose,
    promotionToEdit,
    onSave,
    onPromotionSaved,
    isReadOnly = false,
}) => {
    const { t } = useTranslation();
    const toast = useToast();
    const [formData, setFormData] = React.useState<PromotionFormData>(defaultFormData);
    const [promotionTypes, setPromotionTypes] = React.useState<PromotionTypeOption[]>([]);
    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState<Record<string, string>>({});

    // Load active promotion types
    React.useEffect(() => {
        if (isOpen) {
            getPromotionTypes().then((types) => {
                setPromotionTypes(
                    types.map((t) => ({
                        id: t.id,
                        type_code: t.type_code,
                        type_name: t.type_name,
                        type_desc: t.type_desc ?? undefined,
                        criteria_pattern: t.criteria_pattern ?? undefined,
                        type_priority: t.type_priority,
                    }))
                );
            }).catch(() => {});
        }
    }, [isOpen]);

    // Get the selected promotion type's criteria pattern
    const selectedPromotionType = React.useMemo(() => {
        if (!formData.promo_type_id) return null;
        return promotionTypes.find((pt) => pt.id === formData.promo_type_id) || null;
    }, [formData.promo_type_id, promotionTypes]);

    // Build available criteria types based on the selected type's criteria_pattern
    const allowedCriteriaTypes = React.useMemo(() => {
        if (!selectedPromotionType?.criteria_pattern) return [];
        // location is always allowed (added automatically)
        return [...selectedPromotionType.criteria_pattern, 'location'];
    }, [selectedPromotionType]);

    React.useEffect(() => {
        if (promotionToEdit) {
            setFormData({
                promo_code: promotionToEdit.promo_code,
                promo_name: promotionToEdit.promo_name,
                promo_desc: promotionToEdit.promo_desc,
                promo_type_id: promotionToEdit.promo_type_id,
                promo_datefrom: promotionToEdit.promo_datefrom,
                promo_dateto: promotionToEdit.promo_dateto,
                promo_timefrom: promotionToEdit.promo_timefrom,
                promo_timeto: promotionToEdit.promo_timeto,
                promo_days_mask: promotionToEdit.promo_days_mask,
                promo_active: promotionToEdit.promo_active,
                offer_criteria: promotionToEdit.offer_criteria,
                promo_disper: promotionToEdit.promo_disper,
                promo_disamt: promotionToEdit.promo_disamt,
                promo_free_item: promotionToEdit.promo_free_item,
                promo_free_voucher: promotionToEdit.promo_free_voucher,
            });
        } else {
            setFormData(defaultFormData);
        }
        setErrors({});
    }, [promotionToEdit, isOpen]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Validate promotion type is selected
        if (!formData.promo_type_id) {
            newErrors.promo_type_id = 'Promotion Type is required';
        }

        // Validate mandatory date/time fields
        if (!formData.promo_datefrom) {
            newErrors.promo_datefrom = 'Date From is required';
        }
        if (!formData.promo_dateto) {
            newErrors.promo_dateto = 'Date To is required';
        }
        if (!formData.promo_timefrom) {
            newErrors.promo_timefrom = 'Time From is required';
        }
        if (!formData.promo_timeto) {
            newErrors.promo_timeto = 'Time To is required';
        }

        // Validate date range
        if (formData.promo_datefrom && formData.promo_dateto) {
            if (new Date(formData.promo_dateto) < new Date(formData.promo_datefrom)) {
                newErrors.promo_dateto = 'Date To must be greater than or equal to Date From';
            }
        }

        // Validate time range
        if (formData.promo_timefrom && formData.promo_timeto) {
            if (formData.promo_timeto < formData.promo_timefrom) {
                newErrors.promo_timeto = 'Time To must be greater than or equal to Time From';
            }
        }

        // Validate exclusive discount types
        const discountFields = [
            formData.promo_disper,
            formData.promo_disamt,
            formData.promo_free_item,
            formData.promo_free_voucher
        ].filter(v => v && v.trim() !== '');

        if (discountFields.length > 1) {
            newErrors.discount = 'Only one promotion type can be selected: Discount %, Discount Value, Free Item, or Free Voucher';
        }

        // Validate that all required criteria for the selected type are filled
        if (selectedPromotionType?.criteria_pattern) {
            for (const requiredType of selectedPromotionType.criteria_pattern) {
                const matchingCriteria = formData.offer_criteria.find(
                    (c) => c.criteria_type === requiredType && isConfiguredCriteria(c)
                );
                if (!matchingCriteria) {
                    const labelMap: Record<string, string> = {
                        item: 'Item Selection',
                        item_group: 'Group Selection',
                        payment_mode: 'Payment Mode',
                        amount_range: 'Amount Range',
                        customer_group: 'Customer Group',
                    };
                    newErrors.criteria = `Required criteria "${labelMap[requiredType] || requiredType}" is not configured. Please add and fill it.`;
                    break;
                }
            }
        }

        // Validate exclusive item group/item criteria
        const hasItemGroup = formData.offer_criteria.some(c => c.criteria_type === 'item_group' &&
            c.criteria_value && c.criteria_value.trim() !== '');
        const hasItem = formData.offer_criteria.some(c => c.criteria_type === 'item' &&
            c.criteria_value && c.criteria_value.trim() !== '');

        if (hasItemGroup && hasItem) {
            newErrors.criteria = 'Cannot have both Item Group and Item selection criteria. Use only one.';
        }

        // Validate duplicate criteria types
        const criteriaTypes = formData.offer_criteria
            .filter(isConfiguredCriteria)
            .map(c => c.criteria_type);
        const uniqueTypes = new Set(criteriaTypes);
        if (criteriaTypes.length !== uniqueTypes.size) {
            newErrors.criteria = 'Duplicate criteria types are not allowed. Each criteria type can only be added once.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFieldChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        // When promotion type changes during creation, reset criteria to empty
        // During edit, preserve existing criteria to avoid data loss
        if (field === 'promo_type_id' && !isEditing) {
            setFormData((prev) => ({ ...prev, offer_criteria: [] }));
        }

        // Clear error when field is updated
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }

        if (field === 'offer_criteria' && errors.criteria) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.criteria;
                return newErrors;
            });
        }

        if (
            ['promo_disper', 'promo_disamt', 'promo_free_item', 'promo_free_voucher'].includes(field) &&
            errors.discount
        ) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.discount;
                return newErrors;
            });
        }

        if (field === 'promo_type_id' && errors.promo_type_id) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.promo_type_id;
                return newErrors;
            });
        }
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast.showError(t('page.promotion.drawer.validation.fix_errors', { defaultValue: 'Please fix validation errors' }));
            return;
        }

        try {
            setSaving(true);
            await onSave(toPromoHedRequest(formData));
            onPromotionSaved();
            onClose();
            setErrors({});
        } catch (error: any) {
            // Error handling is done by parent
        } finally {
            setSaving(false);
        }
    };

    const isEditing = !!promotionToEdit;

    return (
        <Drawer
            title={isEditing
                ? t('page.promotion.drawer.edit_title', 'Update Promotion')
                : t('page.promotion.drawer.create_title', 'Create Promotion')
            }
            open={isOpen}
            onClose={onClose}
            width="large"
        >
            <Stack space="medium">
                {/* SECTION 1: Header - Promotion Details */}
                <PromotionHeaderSection
                    promoCode={formData.promo_code}
                    promoName={formData.promo_name}
                    promoDesc={formData.promo_desc}
                    promoDateFrom={formData.promo_datefrom}
                    promoDateTo={formData.promo_dateto}
                    promoTimeFrom={formData.promo_timefrom}
                    promoTimeTo={formData.promo_timeto}
                    promoDaysMask={formData.promo_days_mask}
                    promoActive={formData.promo_active}
                    onChange={handleFieldChange}
                    isReadOnly={isReadOnly}
                    errors={errors}
                />

                {/* Promotion Type Selection */}
                <Card>
                    <Stack space="small">
                        <Text weight="strong" size="large">
                            {t('page.promotion.type.title', 'Promotion Type')}
                        </Text>
                        <Dropdown
                            id="promo-type-id"
                            aria-label="Promotion Type"
                            value={formData.promo_type_id != null ? String(formData.promo_type_id) : ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                handleFieldChange('promo_type_id', val ? Number(val) : null);
                            }}
                            disabled={isReadOnly}
                        >
                            {[
                                <option key="default" value="">-- Select Promotion Type --</option>,
                                ...promotionTypes.map((pt) => (
                                    <option key={pt.id} value={String(pt.id)}>
                                        {`${pt.type_code} - ${pt.type_name}`}
                                    </option>
                                ))
                            ] as any}
                        </Dropdown>
                        {errors?.promo_type_id ? (
                            <Text size="xsmall" tone="critical">{errors.promo_type_id}</Text>
                        ) : undefined}
                        {selectedPromotionType?.type_desc ? (
                            <Box background="neutralLight" padding="small" borderRadius="standard">
                                <Text size="small" tone="secondary">
                                    {selectedPromotionType.type_desc}
                                </Text>
                            </Box>
                        ) : undefined}
                    </Stack>
                </Card>

                {/* SECTION 2: Offer Criteria - only visible after selecting a type */}
                {formData.promo_type_id != null && (
                    <PromotionOfferCriteriaSection
                        criteria={formData.offer_criteria}
                        onChange={(criteria) => handleFieldChange('offer_criteria', criteria)}
                        isReadOnly={isReadOnly}
                        error={errors.criteria}
                        allowedCriteriaTypes={allowedCriteriaTypes}
                        selectedTypeId={formData.promo_type_id}
                    />
                )}

                {formData.promo_type_id == null && !isReadOnly && (
                    <Box paddingY="small">
                        <Text tone="secondary" size="small">
                            Select a promotion type above to configure offer criteria.
                        </Text>
                    </Box>
                )}

                {/* SECTION 3: Promotion (Discount) - Bottom of the page */}
                <PromotionDiscountSection
                    discPercent={formData.promo_disper}
                    discValue={formData.promo_disamt}
                    freeItem={formData.promo_free_item}
                    freeVoucher={formData.promo_free_voucher}
                    onChange={handleFieldChange}
                    isReadOnly={isReadOnly}
                    error={errors.discount}
                />

                {/* Action Buttons */}
                {!isReadOnly && (
                    <Inline space="small" alignY="center" align="right">
                        <Button
                            variant="ghost"
                            onClick={onClose}
                        >
                            {t('page.promotion.drawer.cancel', 'Cancel')}
                        </Button>
                        <Button
                            tone="brandAccent"
                            onClick={handleSave}
                            loading={saving}
                        >
                            {isEditing
                                ? t('page.promotion.drawer.update', 'Update Promotion')
                                : t('page.promotion.drawer.save', 'Save Promotion')
                            }
                        </Button>
                    </Inline>
                )}
            </Stack>
        </Drawer>
    );
};

export default CreateUpdatePromotionDrawer;